import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { lightTheme } from "../../styles/theme";

const COLORS = [
  lightTheme.colors.primary,
  lightTheme.colors.red,
  lightTheme.colors.yellow,
  lightTheme.colors.green,
  lightTheme.colors.blue,
  lightTheme.colors.accent,
  lightTheme.colors.purple,
  lightTheme.colors.teal,
  lightTheme.colors.orange,
  lightTheme.colors.pink,
];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  gravity: number;
  opacity: number;
  decay: number;
}

interface FullScreenConfettiProps {
  duration?: number;
}

const FullScreenConfetti: React.FC<FullScreenConfettiProps> = ({ duration = 3000 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    const startTime = Date.now();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Create particles from two burst points (left-center and right-center)
    const particles: Particle[] = [];
    const count = 200;

    for (let i = 0; i < count; i++) {
      // Alternate burst origin between left and right
      const fromLeft = i % 2 === 0;
      const originX = fromLeft ? canvas.width * 0.1 : canvas.width * 0.9;
      const originY = canvas.height * 0.75;

      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.2;
      const speed = 8 + Math.random() * 14;
      const dirX = fromLeft ? Math.abs(Math.cos(angle)) : -Math.abs(Math.cos(angle));

      particles.push({
        x: originX,
        y: originY,
        vx: dirX * speed + (Math.random() - 0.5) * 4,
        vy: Math.sin(angle) * speed - Math.random() * 6,
        w: 5 + Math.random() * 7,
        h: 3 + Math.random() * 5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 12,
        gravity: 0.08 + Math.random() * 0.06,
        opacity: 1,
        decay: 0.001 + Math.random() * 0.002,
      });
    }

    const draw = () => {
      const elapsed = Date.now() - startTime;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let alive = false;

      for (const p of particles) {
        if (p.opacity <= 0) continue;
        alive = true;

        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.99;
        p.rotation += p.rotationSpeed;

        // Start fading in the last 30% of the duration
        if (elapsed > duration * 0.7) {
          p.opacity -= p.decay * 1.5;
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      if (alive && elapsed < duration) {
        animationId = requestAnimationFrame(draw);
      }
    };

    animationId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, [duration]);

  return ReactDOM.createPortal(
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    />,
    document.body
  );
};

export default FullScreenConfetti;
